// medicine-resolver.service.ts

import { Injectable } from '@angular/core';
import { Medicine } from '../../component/admin/prescriptions/medicine-row.model';
import { ParsedMedicineHint } from './homeopathy-row-parser.service';

interface ScoredMedicine {
  medicine: Medicine;
  score: number;
}

@Injectable({ providedIn: 'root' })
export class MedicineResolverService {

  // Version 1
  // resolve(
  //   hint: ParsedMedicineHint,
  //   allMedicines: Medicine[]
  // ): Medicine | null {

  //   // 1️⃣ Code match (strongest)
  //   if (hint.codeHint) {
  //     const byCode = allMedicines.find(
  //       m => m.code.toLowerCase() === hint.codeHint.toLowerCase()
  //     );
  //     if (byCode) return byCode;
  //   }

  //   // 2️⃣ Name + potency
  //   const nameMatches = allMedicines.filter(m =>
  //     m.name.toLowerCase().includes(hint.nameHint.toLowerCase())
  //   );

  //   if (nameMatches.length === 1) {
  //     return nameMatches[0];
  //   }

  //   if (hint.potencyHint) {
  //     const byPotency = nameMatches.find(
  //       m => m.potency.toLowerCase() === hint.potencyHint.toLowerCase()
  //     );
  //     if (byPotency) return byPotency;
  //   }

  //   // 3️⃣ Safe failure
  //   return null;
  // }

  // Version 2
  // resolveWithIndex(
  // hint: ParsedMedicineHint,
  // medicineByCode: Map<string, Medicine>,
  // medicinesByName: Map<string, Medicine[]>
  // ): Medicine | null {

  //   // 1️⃣ Code match (O(1))
  //   if (hint.codeHint) {
  //     const byCode = medicineByCode.get(hint.codeHint.toLowerCase());
  //     if (byCode) return byCode;
  //   }

  //   // 2️⃣ Name match (still fast)
  //   const nameKey = hint.nameHint.toLowerCase();
  //   const candidates = medicinesByName.get(nameKey);

  //   if (!candidates || candidates.length === 0) return null;

  //   if (candidates.length === 1) {
  //     return candidates[0];
  //   }

  //   // 3️⃣ Potency disambiguation
  //   if (hint.potencyHint) {
  //     const byPotency = candidates.find(
  //       m => m.potency.toLowerCase() === hint.potencyHint.toLowerCase()
  //     );
  //     if (byPotency) return byPotency;
  //   }

  //   return null;
  // }

  private normalize(text: string): string {
    return text
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  private normalizeCode(code: string): string {
    const match = code.toUpperCase().match(/^([A-Z]+)0*(\d+)$/);
    if (!match) return code.toUpperCase();
    return `${match[1]}${match[2]}`;
  }

  private tokenizeName(text: string): string[] {
    return String(text || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .split(' ')
      .map((t) => t.trim())
      .filter((t) => t.length >= 2);
  }

  private levenshteinDistance(a: string, b: string): number {
    const m = a.length;
    const n = b.length;
    if (!m) return n;
    if (!n) return m;

    const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;

    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + cost
        );
      }
    }

    return dp[m][n];
  }

  private nameSimilarity(a: string, b: string): number {
    const x = this.normalize(a);
    const y = this.normalize(b);
    if (!x || !y) return 0;
    if (x === y) return 1;
    const dist = this.levenshteinDistance(x, y);
    return 1 - dist / Math.max(x.length, y.length);
  }

  private tokenOverlapScore(hintName: string, medName: string): number {
    const hintTokens = this.tokenizeName(hintName);
    const medTokens = this.tokenizeName(medName);
    if (!hintTokens.length || !medTokens.length) return 0;

    let matched = 0;
    for (const ht of hintTokens) {
      const ok = medTokens.some((mt) => {
        if (ht === mt) return true;
        if (ht.startsWith(mt) || mt.startsWith(ht)) return true;
        if (ht.length >= 3 && mt.length >= 3 && ht.slice(0, 3) === mt.slice(0, 3)) return true;
        return false;
      });
      if (ok) matched++;
    }

    return matched / hintTokens.length;
  }

  private scoreMedicine(hint: ParsedMedicineHint, med: Medicine): number {
    const nameScore = this.nameSimilarity(hint.nameHint || '', med.name || '');
    const overlapScore = this.tokenOverlapScore(hint.nameHint || '', med.name || '');
    let score = 0.6 * nameScore + 0.25 * overlapScore;

    if (hint.potencyHint) {
      const hp = this.normalize(hint.potencyHint);
      const mp = this.normalize(med.potency || '');
      if (hp && mp && hp === mp) score += 0.15;
    }

    if (hint.codeHint) {
      const hc = this.normalizeCode(hint.codeHint);
      const mc = this.normalizeCode(med.code || '');
      if (hc && mc && hc === mc) score += 0.3;
      if (hc && mc && hc !== mc) score -= 0.1;
    }

    return Math.max(0, Math.min(1, score));
  }

  getClosestMatches(hint: ParsedMedicineHint, allMedicines: Medicine[], topN = 3): ScoredMedicine[] {
    return (allMedicines || [])
      .map((medicine) => ({
        medicine,
        score: this.scoreMedicine(hint, medicine)
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, topN);
  }





  // Version 3
  // resolveWithIndex(
  //   hint: ParsedMedicineHint,
  //   medicineByCode: Map<string, Medicine>,
  //   allMedicines: Medicine[]
  // ): Medicine | null {

  //   // 1️⃣ Code match (always safe)
  //   if (hint.codeHint) {
  //     const byCode = medicineByCode.get(hint.codeHint.toLowerCase());
  //     if (byCode) return byCode;
  //   }

  //   // 2️⃣ Exact name + potency match
  //   if (hint.nameHint && hint.potencyHint) {
  //     const normalizedHint =
  //       this.normalize(hint.nameHint + hint.potencyHint);

  //     const exactMatches = allMedicines.filter(m =>
  //       this.normalize(m.name + m.potency) === normalizedHint
  //     );

  //     if (exactMatches.length === 1) {
  //       return exactMatches[0];
  //     }
  //   }

  //   // 3️⃣ Anything else is AMBIGUOUS → reject
  //   return null;
  // }

  // Version 4
  resolveWithIndex(
    hint: ParsedMedicineHint,
    medicineByCode: Map<string, Medicine>,
    allMedicines: Medicine[]
  ): Medicine | null {

    // -------------------------
    // 1️⃣ CODE MATCH (ABSOLUTE)
    // -------------------------
    if (hint.codeHint) {
      const normalizedHintCode = this.normalizeCode(hint.codeHint);
      const byCode = medicineByCode.get(normalizedHintCode);
      if (byCode) {
        return byCode; // DONE
      }
    }

    // -------------------------
    // 2️⃣ NAME + POTENCY MATCH
    // -------------------------
    if (hint.nameHint && hint.potencyHint) {
      const normalizedHintName = this.normalize(hint.nameHint);
      const normalizedHintPotency = this.normalize(hint.potencyHint);

      const candidates = allMedicines.filter(m =>
        this.normalize(m.name).includes(normalizedHintName) &&
        this.normalize(m.potency) === normalizedHintPotency
      );

      if (candidates.length === 1) {
        return candidates[0];
      }
    }

    // -------------------------
    // 3️⃣ POTENCY + CODE FALLBACK (rare but useful)
    // -------------------------
    if (hint.potencyHint && hint.codeHint) {
      const candidates = allMedicines.filter(m =>
        this.normalize(m.code) === this.normalize(hint.codeHint) &&
        this.normalize(m.potency) === this.normalize(hint.potencyHint)
      );

      if (candidates.length === 1) {
        return candidates[0];
      }
    }

    // -------------------------
    // 4️⃣ GIVE UP SAFELY
    // -------------------------
    return null;
  }

  resolveWithConfidence(
    hint: ParsedMedicineHint,
    medicineByCode: Map<string, Medicine>,
    allMedicines: Medicine[]
  ): Medicine | null {
    const strict = this.resolveWithIndex(hint, medicineByCode, allMedicines);
    if (strict) return strict;

    const closest = this.getClosestMatches(hint, allMedicines, 2);
    const first = closest[0];
    const second = closest[1];

    if (!first) return null;

    const minConfidence = 0.82;
    const minGap = 0.08;
    const gap = second ? first.score - second.score : first.score;

    if (first.score >= minConfidence && gap >= minGap) {
      return first.medicine;
    }

    return null;
  }


}
